import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useRef } from "react";
import { integrationData } from "../../constants";
import { Limitation } from "../../types";

interface FormField {
  label: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  isRequired?: boolean;
  limitation?: Limitation;
}

interface GenericConfigModalProps {
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  connectorType: string | null;
  fields: FormField[];
}

export const GenericConfigModal = (props: GenericConfigModalProps) => {
  const { isOpen, onClose, onSubmit, connectorType, fields, isLoading } = props;
  const initialRef = useRef(null);
  /* Match http/https optionally, domain name (+ subdomain) or IPv4 address, and anything after / */
  const urlPattern =
    /^(https?:\/\/)?((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})|((2[0-5]{2}|1[0-9]{2}|[0-9]{1,2}|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(2[0-5]{2}|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9]))(\/[^\s]*)?$/;

  const validateField = (field: FormField) => {
    let value = field.value;

    switch (field.limitation) {
      case Limitation.PORT: {
        let portNumber = parseInt(value);
        return portNumber >= 0 && portNumber <= 65535;
      }
      case Limitation.URL: {
        return urlPattern.test(value);
      }
    }

    return true;
  };

  const isFormValid = fields.every((field) => !field.isRequired || (field.value.trim() !== "" && validateField(field)));

  return (
    <Modal closeOnOverlayClick={false} scrollBehavior="inside" variant="large" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent gap="0" padding="0">
        {connectorType && (
          <ModalHeader pt="1rem" pb="2rem" px="1.5rem" border="none">
            <Text textStyle="heading/3" fontSize="lg" width="90%">
              <Box display="inline-block" height="1.25rem" width="1.25rem" mr="0.5rem" mb="-0.300rem">
                {integrationData[connectorType].logo}
              </Box>
              {integrationData[connectorType].title}
            </Text>
          </ModalHeader>
        )}
        <ModalBody display="flex" flexDirection="column" gap="spacing-md" px="1.5rem">
          {fields.map((field, index) => (
            <FormControl key={index}>
              <FormLabel as="legend">{field.label}</FormLabel>
              {field.type === "textarea" ? (
                <Textarea
                  placeholder={field.placeholder}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              ) : (
                <Input
                  ref={index === 0 ? initialRef : null}
                  type={field.type || "text"}
                  placeholder={field.placeholder}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            </FormControl>
          ))}
        </ModalBody>
        <ModalFooter pt="2rem" pb="1rem">
          <Button onClick={onClose} mr={2} variant="ghost">
            Cancel
          </Button>
          <Button isLoading={isLoading} variant="primary" isDisabled={!isFormValid} onClick={onSubmit}>
            Create Connection
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
